import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { Query } from '../../../generated/internal.graphql';
import type { VPResult } from '../../VPResult';
import { isUnauthorized, type InternalApiError, toApiErrors, UNAUTHORIZED } from '../errors';
import * as CurrentUser from './CurrentUser';
import { ACCOUNT_AGNOSTIC } from '../../accountContext';

// The 401 classification is shared by every internal domain and now lives in ../errors.
// Re-exported here so existing importers of this module keep working.
export { isUnauthorized, type InternalApiError, toApiErrors, UNAUTHORIZED };

const NO_USER_MESSAGE = 'The internal schema returned no staff user';

export const CURRENT_USER_QUERY = gql`
  query internalCurrentUser {
    currentUser {
      id
      email
      name
      superadmin
    }
  }
`;

export const fetchCurrentUser = async (
  client: ApolloClient<NormalizedCacheObject>,
): Promise<VPResult<CurrentUser.CurrentUser, InternalApiError[]>> => {
  try {
    const { data } = await client.query<Query>({
      query: CURRENT_USER_QUERY,
      fetchPolicy: 'network-only',
      // Account-agnostic: the staff operator's own identity. Not AppSchema's `currentUser`, which
      // selects `accountConfig` and stays scoped.
      context: ACCOUNT_AGNOSTIC,
    });

    if (!data?.currentUser) {
      return { success: false, errors: [{ path: '', message: NO_USER_MESSAGE }] };
    }

    return { success: true, data: CurrentUser.fromGql(data.currentUser) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};
